import React from 'react';
import { Modal, Spinner, Form, Button } from 'react-bootstrap';
import { AlertTriangle, Check, CheckCircle, XCircle, Download, BookOpen, CreditCard, Edit2, ExternalLink, X, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import * as api from '../../../../services/api/expensesApi';
import * as S from '../../Common/internalToolsStyles';
import { useCapabilities } from '../../../../context/CapabilityContext';
import BookExpenseModal from './BookExpenseModal';
import SettleExpenseModal from './SettleExpenseModal';

/**
 * Detail / edit modal for a single expense.
 *
 * Shared between:
 *   - ExpensesPage (submitter view) — click a kanban card to open
 *   - ExpenseApprovalQueuePage (admin view) — click a row to open, plus
 *     approve/reject actions in the footer when status='submitted'
 *
 * Edit gate matches the backend rule: allowed unless status='approved' OR
 * the expense is already linked to a payroll / customer invoice.
 */

// Swedish SMB category labels. Kept inline so this module has no dependency
// on ExpensesPage's CATEGORIES constant.
const VAT_RATES = [25, 12, 6, 0];
const nearestVatRate = (r) => {
  if (r == null || Number.isNaN(Number(r))) return 25;
  return VAT_RATES.reduce((best, v) => Math.abs(v - r) < Math.abs(best - r) ? v : best, 25);
};

const CATEGORIES = [
  { value: 'travel',         label: 'Resa',                 icon: '✈️' },
  { value: 'taxi',           label: 'Taxi',                 icon: '🚕' },
  { value: 'accommodation',  label: 'Boende',               icon: '🏨' },
  { value: 'meals',          label: 'Mat & dryck',          icon: '🍽️' },
  { value: 'fika',           label: 'Fika / enklare förtäring', icon: '☕' },
  { value: 'representation', label: 'Representation',       icon: '🤝' },
  { value: 'mileage',        label: 'Milersättning',        icon: '🚗' },
  { value: 'parking',        label: 'Parkering',            icon: '🅿️' },
  { value: 'fuel',           label: 'Drivmedel',            icon: '⛽' },
  { value: 'software',       label: 'Programvara',          icon: '💻' },
  { value: 'equipment',      label: 'Utrustning',           icon: '🔧' },
  { value: 'materials',      label: 'Material',             icon: '📦' },
  { value: 'phone_internet', label: 'Telefon & internet',   icon: '📱' },
  { value: 'training',       label: 'Utbildning',           icon: '📚' },
  { value: 'conference',     label: 'Konferens',            icon: '🎤' },
  { value: 'wellness',       label: 'Friskvård',            icon: '🏃' },
  { value: 'other',          label: 'Övrigt',               icon: '📋' },
];
const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

const fmtAmount = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

// Status → soft bg + strong fg for the header icon chip.
// Mirrors CATEGORY_META in DocumentsPage — one-look colour hint without
// needing a per-category palette.
const STATUS_META = {
  draft:     { bg: '#f1f5f9', color: '#475569', label: 'Utkast' },
  submitted: { bg: '#fef3c7', color: '#92400e', label: 'Väntar godkännande' },
  approved:  { bg: '#d1fae5', color: '#065f46', label: 'Godkänd' },
  rejected:  { bg: '#fee2e2', color: '#991b1b', label: 'Avvisad' },
  booked:    { bg: '#dbeafe', color: '#1e40af', label: 'Bokförd' },
  settled:   { bg: '#d1fae5', color: '#065f46', label: 'Betald' },
};

// Stable component ref — defining FieldRow inside the parent would cause
// React to remount inputs on every keystroke and lose focus.
const FieldRow = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#111827' }}>{children}</div>
  </div>
);

const ExpenseDetailFields = ({ expense, edit, setEdit, isEditing, projects }) => {
  if (!isEditing) {
    const vat = expense.metadata?.vat || {};
    const vatAmount = vat.amount != null ? vat.amount : null;
    const vatRate = vat.rate != null ? vat.rate : null;
    const netAmount = (vatAmount != null && Number(expense.amount))
      ? Number(expense.amount) - Number(vatAmount)
      : null;
    return (
      <>
        <FieldRow label="Datum">{format(new Date(expense.expense_date), 'd MMM yyyy')}</FieldRow>
        <FieldRow label="Belopp (brutto)">{fmtAmount(expense.amount)} {expense.currency || 'SEK'}</FieldRow>
        <FieldRow label="Moms">
          {vatAmount != null
            ? <>{fmtAmount(vatAmount)} {expense.currency || 'SEK'} ({vatRate != null ? `${vatRate}%` : '—'})
                {netAmount != null && <span style={{ color: '#6b7280', marginLeft: 8 }}>Netto {fmtAmount(netAmount)}</span>}</>
            : <span style={{ color: '#dc2626' }}>Saknas</span>}
        </FieldRow>
        <FieldRow label="Beskrivning">{expense.description || '—'}</FieldRow>
        <FieldRow label="Kategori">{catMap[expense.category]?.label || expense.category}</FieldRow>
        <FieldRow label="Projekt">{expense.project_name || '—'}</FieldRow>
        <FieldRow label="Debiterbar / Ersättningsbar">
          {expense.is_billable ? '✓ Debiterbar' : '—'} · {expense.is_reimbursable ? '✓ Ersättningsbar' : '—'}
        </FieldRow>
        {expense.notes && <FieldRow label="Anteckningar">{expense.notes}</FieldRow>}
      </>
    );
  }

  const set = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <>
      <FieldRow label="Datum">
        <Form.Control type="date" value={edit.expenseDate} onChange={(e) => set('expenseDate', e.target.value)} size="sm" />
      </FieldRow>
      <FieldRow label="Belopp (brutto, SEK)">
        <Form.Control type="number" step="0.01" min="0" value={edit.amount} onChange={(e) => set('amount', e.target.value)} size="sm" />
      </FieldRow>
      <FieldRow label="Moms (SEK)">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Form.Control
            type="number" step="0.01" min="0"
            value={edit.vatAmount}
            onChange={(e) => set('vatAmount', e.target.value)}
            size="sm"
            style={{ maxWidth: 160 }}
            required
          />
          <Form.Select
            value={edit.vatRate}
            onChange={(e) => set('vatRate', e.target.value)}
            size="sm"
            style={{ maxWidth: 110 }}
          >
            {VAT_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
          </Form.Select>
          <Button
            type="button" size="sm" variant="outline-secondary"
            title="Beräkna moms från brutto och sats"
            onClick={() => {
              const gross = parseFloat(edit.amount) || 0;
              const rate = parseInt(edit.vatRate, 10) || 0;
              if (!gross || !rate) return;
              const computed = (gross * rate) / (100 + rate);
              setEdit({ ...edit, vatAmount: computed.toFixed(2) });
            }}
          >
            Beräkna
          </Button>
        </div>
        {edit.vatRate === '0' && (
          <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>
            0% moms — ovanligt för svenska kvitton. Bekräfta att kvittot är momsfritt.
          </div>
        )}
      </FieldRow>
      <FieldRow label="Beskrivning">
        <Form.Control type="text" value={edit.description} onChange={(e) => set('description', e.target.value)} size="sm" />
      </FieldRow>
      <FieldRow label="Kategori">
        <Form.Select value={edit.category} onChange={(e) => set('category', e.target.value)} size="sm">
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
        </Form.Select>
      </FieldRow>
      <FieldRow label="Projekt">
        <Form.Select value={edit.projectId || ''} onChange={(e) => set('projectId', e.target.value)} size="sm">
          <option value="">Inget projekt</option>
          {(projects || []).map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
        </Form.Select>
      </FieldRow>
      <FieldRow label="Flaggor">
        <label style={{ fontSize: 13, marginRight: 12 }}>
          <input type="checkbox" checked={edit.isBillable} onChange={(e) => set('isBillable', e.target.checked)} style={{ marginRight: 4 }} />
          Debiterbar
        </label>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={edit.isReimbursable} onChange={(e) => set('isReimbursable', e.target.checked)} style={{ marginRight: 4 }} />
          Ersättningsbar
        </label>
      </FieldRow>
      <FieldRow label="Anteckningar">
        <Form.Control as="textarea" rows={2} value={edit.notes || ''} onChange={(e) => set('notes', e.target.value)} size="sm" />
      </FieldRow>
    </>
  );
};

const ExpenseDetailModal = ({ expense, companyId, projects, onClose, onChanged, adminMode = false }) => {
  const { capabilities } = useCapabilities();
  // Phase 2.4 F4 — when the admin viewing this modal IS the row's employee,
  // the workflow buttons (Approve/Reject/Book/Unbook/Settle) are disabled
  // with a tooltip. Server still 403s via assertCanApproveExpense's row-
  // level F4 guard (Phase 1.2.3); this is the convenience layer.
  const isOwnExpense = adminMode
    && !!capabilities.employeeId
    && !!expense?.employee_id
    && expense.employee_id === capabilities.employeeId;
  const ownDisabledTitle = 'Du kan inte godkänna ditt eget utlägg.';
  const [edit, setEdit] = React.useState(null);
  const [editError, setEditError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [receiptUrl, setReceiptUrl] = React.useState(null);
  const [bookingOpen, setBookingOpen] = React.useState(false);
  const [settleOpen, setSettleOpen] = React.useState(false);
  // Audit trail — verifikat(er) linked to this expense. Fetched only when
  // the admin views a booked/settled expense so the read path doesn't
  // cost anything for the employee or for pending expenses.
  const [linkedVerifications, setLinkedVerifications] = React.useState([]);

  React.useEffect(() => {
    if (!expense || !adminMode) { setLinkedVerifications([]); return undefined; }
    if (expense.status !== 'booked' && expense.status !== 'settled') {
      setLinkedVerifications([]); return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.listExpenseVerifications(companyId, expense.expense_id);
        if (!cancelled) setLinkedVerifications(res.data?.verifications || []);
      } catch { /* silent — audit trail is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [companyId, expense?.expense_id, expense?.status, adminMode]);

  React.useEffect(() => {
    if (!expense?.receipt_url) { setReceiptUrl(null); return undefined; }
    let objUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const blobName = decodeURIComponent(new URL(expense.receipt_url).pathname.split('/').pop());
        const path = `${companyId}/documents/${encodeURIComponent(blobName)}/content`;
        const res = await axios.get(getApiUrl(path), { responseType: 'blob', withCredentials: true });
        objUrl = URL.createObjectURL(res.data);
        if (!cancelled) setReceiptUrl(objUrl);
      } catch { /* silent — fallback shown below */ }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [expense?.receipt_url, expense?.expense_id, companyId]);

  React.useEffect(() => {
    if (!expense) { setEdit(null); return; }
    setEdit(null); // read-only by default on open
  }, [expense?.expense_id]);

  if (!expense) return null;
  const cat = catMap[expense.category] || { label: expense.category, icon: '📋' };

  // Edit gate — once an expense has been approved / booked / settled /
  // payrolled / invoiced, the ledger or an upstream committee depends on
  // its values and editing would desync them. Only the employee-controlled
  // states remain editable: draft, submitted (before the admin touches it),
  // rejected (so the employee can fix and resubmit).
  const canEdit = ['draft', 'submitted', 'rejected'].includes(expense.status)
    && !expense.payroll_id
    && !expense.invoice_item_id;
  const isEditing = edit !== null;
  const startEdit = () => {
    const existingVat = expense.metadata?.vat || {};
    setEditError(null);
    setEdit({
      expenseDate: expense.expense_date?.slice(0, 10) || format(new Date(), 'yyyy-MM-dd'),
      amount: String(expense.amount ?? ''),
      vatAmount: existingVat.amount != null ? String(existingVat.amount) : '',
      vatRate: existingVat.rate != null ? String(nearestVatRate(existingVat.rate)) : '25',
      category: expense.category || 'other',
      description: expense.description || '',
      projectId: expense.project_id || '',
      isBillable: !!expense.is_billable,
      isReimbursable: !!expense.is_reimbursable,
      notes: expense.notes || '',
    });
  };
  const cancelEdit = () => { setEditError(null); setEdit(null); };
  const saveEdit = async () => {
    setEditError(null);
    if (edit.vatAmount === '' || edit.vatRate === '') {
      setEditError('Moms och momssats måste anges.');
      return;
    }
    setSaving(true);
    try {
      const mergedMetadata = {
        ...(expense.metadata || {}),
        vat: {
          amount: parseFloat(edit.vatAmount) || 0,
          rate: parseInt(edit.vatRate, 10) || 0,
        },
      };
      await api.updateExpense(companyId, expense.expense_id, {
        expenseDate: edit.expenseDate,
        amount: parseFloat(edit.amount),
        vatAmount: parseFloat(edit.vatAmount) || 0,
        vatRate: parseInt(edit.vatRate, 10) || 0,
        category: edit.category,
        description: edit.description,
        projectId: edit.projectId || null,
        isBillable: edit.isBillable,
        isReimbursable: edit.isReimbursable,
        notes: edit.notes || null,
        metadata: mergedMetadata,
      });
      await onChanged();
    } catch (err) {
      setEditError(err.response?.data?.message || 'Kunde inte spara.');
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      await api.approveExpense(companyId, expense.expense_id);
      await onChanged();
    } catch { setSaving(false); }
  };

  const handleReject = async () => {
    const { value: reason } = await Swal.fire({
      title: 'Avvisa utlägg',
      input: 'textarea',
      inputPlaceholder: 'Ange skäl (visas för den anställde)…',
      inputValidator: (v) => (!v || !v.trim()) ? 'Skäl krävs.' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Avvisa',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545',
    });
    if (!reason) return;
    setSaving(true);
    try {
      await api.rejectExpense(companyId, expense.expense_id, reason.trim());
      await onChanged();
    } catch { setSaving(false); }
  };

  const handleUnbook = async () => {
    const { value: reason } = await Swal.fire({
      title: 'Ångra bokföring',
      text: 'Ett återföringsverifikat skapas som upphäver det tidigare bokföringsverifikatet. Status återgår till godkänd.',
      input: 'textarea',
      inputPlaceholder: 'Ange skäl (loggas på verifikatet)…',
      inputValidator: (v) => (!v || !v.trim()) ? 'Skäl krävs.' : undefined,
      showCancelButton: true,
      confirmButtonText: 'Ångra bokföring',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#6c757d',
    });
    if (!reason) return;
    setSaving(true);
    try {
      const res = await api.unbookExpense(companyId, expense.expense_id, { reason: reason.trim() });
      const vNum = `${res.data?.series || 'V'}${res.data?.verificationNumber}`;
      await Swal.fire({
        icon: 'success',
        title: 'Bokföring ångrad',
        text: `Återföringsverifikat ${vNum} skapat.`,
        timer: 2500,
        showConfirmButton: false,
      });
      await onChanged();
    } catch (err) {
      setSaving(false);
      await Swal.fire({
        icon: 'error',
        title: 'Kunde inte ångra bokföring',
        text: err.response?.data?.message || 'Försök igen.',
      });
    }
  };

  const isImage = (expense.receipt_url || '').match(/\.(png|jpe?g|gif|webp)(\?|$)/i);
  const isPdf   = (expense.receipt_url || '').match(/\.pdf(\?|$)/i);

  const statusMeta = STATUS_META[expense.status] || STATUS_META.draft;
  const submitterName = expense.employee_firstname || expense.employee_lastname
    ? `${expense.employee_firstname || ''} ${expense.employee_lastname || ''}`.trim()
    : null;

  const openReceiptInNewTab = async () => {
    if (!receiptUrl) return;
    window.open(receiptUrl, '_blank', 'noopener,noreferrer');
  };
  const downloadReceipt = () => {
    if (!receiptUrl) return;
    const a = document.createElement('a');
    a.href = receiptUrl;
    a.download = `kvitto-${expense.expense_id || 'fil'}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Modal show={!!expense} onHide={onClose} size="xl" centered dialogClassName="exp-preview-modal">
      {/* Branded header — mirrors DocumentsPage PreviewModal */}
      <div className="exp-header">
        <button className="exp-close" onClick={onClose} aria-label="Stäng (Esc)">
          <X size={16} />
        </button>
        <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
          <div className="exp-header-icon" style={{ background: statusMeta.bg, color: statusMeta.color }}>
            <span style={{ fontSize: 22 }}>{cat.icon}</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="exp-title" title={isEditing ? edit.description : expense.description}>
              {isEditing ? (edit.description || '—') : (expense.description || '—')}
            </div>
            <div className="exp-subtitle">
              <span className="exp-status-badge" style={{ background: statusMeta.bg, color: statusMeta.color }}>
                {statusMeta.label}
              </span>
              <span>·</span>
              <span>{cat.label}</span>
              <span>·</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#0f172a' }}>
                {fmtAmount(expense.amount)} {expense.currency || 'SEK'}
              </span>
              {submitterName && adminMode && (<><span>·</span><span>{submitterName}</span></>)}
            </div>
          </div>
        </div>
      </div>

      <Modal.Body style={{ padding: 0 }}>
        <div className="exp-body" style={{ gridTemplateColumns: expense.receipt_url ? '1fr 380px' : '1fr' }}>
          {expense.receipt_url && (
            <div className="exp-stage">
              {!receiptUrl ? (
                <div className="exp-loading"><Spinner animation="border" size="sm" /></div>
              ) : isImage ? (
                <img src={receiptUrl} alt="Kvitto" />
              ) : isPdf ? (
                <iframe src={receiptUrl} title="Kvitto" />
              ) : (
                <div className="exp-fallback">
                  <div className="exp-fallback-text">Kan inte förhandsvisa filtypen.</div>
                  <Button variant="primary" size="sm" className="mt-2" onClick={downloadReceipt}>
                    <Download size={14} className="me-1" /> Ladda ner kvitto
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="exp-meta">
            <ExpenseDetailFields
              expense={expense} edit={edit} setEdit={setEdit}
              isEditing={isEditing} projects={projects}
            />

            {isEditing && editError && (
              <div className="exp-alert exp-alert-warn">
                <AlertTriangle size={14} /> {editError}
              </div>
            )}

            {expense.rejection_reason && (
              <div className="exp-alert exp-alert-warn">
                <AlertTriangle size={14} /> <strong>Avvisad:</strong>&nbsp;{expense.rejection_reason}
              </div>
            )}
            {expense.payroll_id && (
              <div className="exp-alert exp-alert-info">
                Redan knuten till en lönekörning — kan inte ändras.
              </div>
            )}
            {expense.invoice_item_id && (
              <div className="exp-alert exp-alert-info">
                Redan fakturerad till en kund — kan inte ändras.
              </div>
            )}

            {expense.metadata?.ocr && (expense.metadata.ocr.vendorTaxId || expense.metadata.ocr.subtotal != null) && (
              <div className="exp-ocr">
                <div className="exp-ocr-label">OCR-detaljer</div>
                {expense.metadata.ocr.subtotal != null && (
                  <div>Netto: {Number(expense.metadata.ocr.subtotal).toFixed(2)} · Moms: {Number(expense.metadata.ocr.vatAmount || 0).toFixed(2)}
                    {expense.metadata.ocr.vatRate != null && ` (${expense.metadata.ocr.vatRate}%)`}</div>
                )}
                {expense.metadata.ocr.vendorTaxId && <div>Org-/momsnr: {expense.metadata.ocr.vendorTaxId}</div>}
                {expense.metadata.ocr.invoiceNumber && <div>Fakturanr: {expense.metadata.ocr.invoiceNumber}</div>}
              </div>
            )}

            {/* Admin audit trail — lists verifikat(er) linked to this
                expense via verification_references. Only fetched for
                booked/settled expenses in adminMode. */}
            {adminMode && linkedVerifications.length > 0 && (
              <div className="exp-ocr" style={{ borderColor: '#dbeafe', background: '#eff6ff' }}>
                <div className="exp-ocr-label" style={{ color: '#1e40af' }}>Verifikationer</div>
                {linkedVerifications.map((v) => (
                  <div key={`${v.reference_type}-${v.verification_id}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                    <span style={{ fontFamily: "'SF Mono', Monaco, monospace", fontWeight: 600, color: '#0f172a' }}>
                      {v.series}{v.number}
                    </span>
                    <span style={{ color: '#64748b', fontSize: 11 }}>
                      {v.reference_type === 'expense_booking' ? 'Bokföring' : 'Utbetalning'}
                    </span>
                    <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: 11 }}>
                      {v.date ? format(new Date(v.date), 'd MMM yyyy') : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <div className="exp-footer">
        <div className="exp-footer-hint">Esc stänger</div>
        <div className="exp-footer-actions">
          {isEditing ? (
            <>
              <Button variant="light" onClick={cancelEdit} disabled={saving}>Avbryt</Button>
              <Button variant="primary" onClick={saveEdit} disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" /> : <><Check size={14} className="me-1" /> Spara</>}
              </Button>
            </>
          ) : (
            <>
              {/* Receipt utility icon buttons — only when a receipt exists */}
              {expense.receipt_url && receiptUrl && (
                <>
                  <button className="exp-icon-btn" onClick={openReceiptInNewTab} title="Öppna kvitto i ny flik" aria-label="Öppna kvitto i ny flik">
                    <ExternalLink size={16} />
                  </button>
                  <button className="exp-icon-btn" onClick={downloadReceipt} title="Ladda ner kvitto" aria-label="Ladda ner kvitto">
                    <Download size={16} />
                  </button>
                </>
              )}
              {canEdit && (
                <button className="exp-icon-btn" onClick={startEdit} title="Redigera" aria-label="Redigera">
                  <Edit2 size={16} />
                </button>
              )}
              {/* Workflow actions — admin approve/reject, book, settle.
                  Phase 2.4 F4: own-expense disables every workflow button
                  with a tooltip. Server enforces via assertCanApproveExpense. */}
              {adminMode && expense.status === 'submitted' && (
                <>
                  <Button
                    variant="outline-danger"
                    onClick={handleReject}
                    disabled={saving || isOwnExpense}
                    title={isOwnExpense ? ownDisabledTitle : undefined}
                  >
                    <XCircle size={14} className="me-1" /> Avvisa
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleApprove}
                    disabled={saving || isOwnExpense}
                    title={isOwnExpense ? ownDisabledTitle : undefined}
                  >
                    {saving ? <Spinner size="sm" animation="border" /> : <><CheckCircle size={14} className="me-1" /> Godkänn</>}
                  </Button>
                </>
              )}
              {adminMode && expense.status === 'approved' && !expense.payroll_id && !expense.invoice_item_id && (
                <Button
                  variant="primary"
                  onClick={() => setBookingOpen(true)}
                  disabled={saving || isOwnExpense}
                  title={isOwnExpense ? ownDisabledTitle : undefined}
                >
                  <BookOpen size={14} className="me-1" /> Bokför
                </Button>
              )}
              {adminMode && expense.status === 'booked' && !expense.payroll_id && (
                <>
                  <Button
                    variant="outline-secondary"
                    onClick={handleUnbook}
                    disabled={saving || isOwnExpense}
                    title={isOwnExpense ? ownDisabledTitle : 'Ångra bokföring'}
                  >
                    <RotateCcw size={14} className="me-1" /> Ångra
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setSettleOpen(true)}
                    disabled={saving || isOwnExpense}
                    title={isOwnExpense ? ownDisabledTitle : undefined}
                  >
                    <CreditCard size={14} className="me-1" /> Betala
                  </Button>
                </>
              )}
              {adminMode && expense.status === 'settled' && (
                <span style={{ fontSize: 12, color: '#065f46', fontWeight: 600, padding: '0 8px' }}>
                  <CheckCircle size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
                  Betald
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <style>{expenseDetailModalCss}</style>

      <BookExpenseModal
        companyId={companyId}
        expenseId={expense.expense_id}
        show={bookingOpen}
        onClose={() => setBookingOpen(false)}
        onBooked={async () => { setBookingOpen(false); await onChanged?.(); }}
      />

      <SettleExpenseModal
        companyId={companyId}
        expenseId={expense.expense_id}
        show={settleOpen}
        onClose={() => setSettleOpen(false)}
        onSettled={async () => { setSettleOpen(false); await onChanged?.(); }}
      />
    </Modal>
  );
};

// Styles — mirrors DocumentsPage PreviewModal (white branded header, neutral
// stage, meta grid, light footer). Scoped with .exp-* so it doesn't collide
// with the docs .pv-* variant.
const expenseDetailModalCss = `
  .exp-preview-modal .modal-dialog { max-width: 1100px; }
  .exp-preview-modal .modal-content { border: none; border-radius: 12px; overflow: hidden; }

  .exp-header {
    background: white; padding: 18px 56px 18px 24px;
    border-bottom: 1px solid #e2e8f0;
    position: relative; display: flex; align-items: center;
  }
  .exp-close {
    position: absolute; top: 14px; right: 16px;
    background: white; border: 1px solid #e2e8f0; color: #64748b;
    width: 32px; height: 32px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.2s ease;
  }
  .exp-close:hover { background: #f1f5f9; color: #111827; }
  .exp-header-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .exp-title {
    font-size: 1.05rem; font-weight: 600; color: #0f172a;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .exp-subtitle {
    margin-top: 4px; font-size: 0.75rem; color: #64748b;
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .exp-status-badge {
    padding: 2px 8px; border-radius: 999px; font-weight: 600;
    font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em;
  }

  .exp-body {
    display: grid; min-height: 460px;
  }

  /* Stage — neutral light background so PDF viewer chrome doesn't look
     like a cutout. Matches docs .pv-stage. */
  .exp-stage {
    background: #f3f4f6; position: relative;
    display: flex; align-items: center; justify-content: center;
    min-height: 65vh; max-height: 78vh; overflow: auto;
    border-right: 1px solid #e5e7eb;
  }
  .exp-stage img { max-width: 100%; max-height: 78vh; object-fit: contain; display: block; }
  .exp-stage iframe { width: 100%; height: 78vh; border: 0; background: white; }
  .exp-loading { color: #6b7280; }
  .exp-fallback { text-align: center; padding: 32px; color: #6b7280; }
  .exp-fallback-text { font-size: 0.875rem; color: #6b7280; margin-bottom: 8px; }

  .exp-meta {
    padding: 20px 24px; overflow: auto; max-height: 78vh;
    background: white;
  }
  .exp-alert {
    margin-top: 12px; display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; border-radius: 6px; font-size: 0.75rem; font-weight: 500;
  }
  .exp-alert-warn { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
  .exp-alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }

  .exp-ocr {
    margin-top: 14px; padding: 10px 12px; border-radius: 6px;
    border: 1px solid #e5e7eb; background: #fafafa;
    font-size: 0.75rem; color: #374151;
  }
  .exp-ocr-label {
    font-weight: 600; margin-bottom: 4px;
    text-transform: uppercase; letter-spacing: 0.04em;
    font-size: 0.6875rem; color: #6b7280;
  }

  .exp-footer {
    background: #f8fafc; padding: 12px 24px; border-top: 1px solid #e2e8f0;
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px; flex-wrap: wrap;
  }
  .exp-footer-hint {
    font-size: 0.75rem; color: #64748b;
    font-variant-numeric: tabular-nums;
  }
  .exp-footer-actions { display: flex; align-items: center; gap: 8px; }

  .exp-icon-btn {
    width: 36px; height: 36px; border-radius: 8px;
    background: white; border: 1px solid #d1d5db; color: #374151;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s ease;
  }
  .exp-icon-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #9ca3af; color: #0f172a; }
  .exp-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 900px) {
    .exp-body { grid-template-columns: 1fr !important; }
    .exp-stage { border-right: 0; border-bottom: 1px solid #e5e7eb; }
  }
`;

export default ExpenseDetailModal;
